/**
 * @file 学びメモのプレビューの図 (mermaid) と画像を、画面いっぱいの重ね表示で拡大して見る。
 *
 * @what: 暗い幕の上に図か画像を1つ出し、画面に収まる大きさで開く。「−」「＋」で倍率を変え、
 *   はみ出した分はスクロールで見る。「閉じる」・Esc・図の外を押すと閉じる。描画先は body の直下にする。
 * @why: プレビューの欄は狭く、mermaid の図と貼った画像が欄の幅に縮んで読めない (代表の指示、
 *   2026-11-03。写真の拡大と同じ操作にする)。学びメモの全画面 (z-index 1000) の上にも出す。
 * @failure: 描画のみ。図の大きさが読めないときは等倍で開く。
 */

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./study.css";

export type NoteZoomTarget =
  | { kind: "diagram"; svg: string }
  | { kind: "image"; src: string; alt: string };

const MIN_SCALE = 0.25;
const MAX_SCALE = 5;
const STEP = 1.25;
/** 開いたときの倍率の上限である。図は小さくても読める大きさまで広げ、画像は粗くなるので等倍で止める。 */
const FIT_MAX = { diagram: 2, image: 1 } as const;

/** study.css の .study-note-zoom-stage と .study-note-zoom-content--diagram の padding と合わせる。 */
const STAGE_PADDING = 16;
const DIAGRAM_PADDING = 12;

const clamp = (v: number) => Math.min(MAX_SCALE, Math.max(MIN_SCALE, v));

/** 図 (SVG) のもとの大きさ。viewBox を優先し、無ければ画面に出ている大きさを使う。 */
function svgSize(svg: SVGSVGElement): { w: number; h: number } | null {
  const vb = svg.viewBox?.baseVal;
  if (vb && vb.width > 0 && vb.height > 0) return { w: vb.width, h: vb.height };
  const r = svg.getBoundingClientRect();
  return r.width > 0 && r.height > 0 ? { w: r.width, h: r.height } : null;
}

export function NoteZoomOverlay({
  target,
  onClose,
}: {
  target: NoteZoomTarget;
  onClose: () => void;
}) {
  const stageRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState<{ w: number; h: number } | null>(null);
  const [scale, setScale] = useState(1);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // 後ろの画面を動かさず、Esc で閉じる。Esc は学びメモの全画面には届かせない (先に受けて止める)。
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      onCloseRef.current();
    };
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  // もとの大きさを測り、画面に収まる倍率で開く
  const fitTo = (natural: { w: number; h: number }) => {
    const stage = stageRef.current;
    setSize(natural);
    if (stage === null) return;
    // 幕の余白と、図の下に敷く白い面の余白を除いた広さに収める
    const inset = 2 * STAGE_PADDING + (target.kind === "diagram" ? 2 * DIAGRAM_PADDING : 0);
    const fit = Math.min(
      (stage.clientWidth - inset) / natural.w,
      (stage.clientHeight - inset) / natural.h,
    );
    setScale(clamp(Math.min(fit, FIT_MAX[target.kind])));
  };
  useLayoutEffect(() => {
    if (target.kind !== "diagram") return;
    const svg = contentRef.current?.querySelector("svg");
    const natural = svg ? svgSize(svg) : null;
    if (natural) fitTo(natural);
    // 開いたときに1度だけ測る
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const box = size ? { width: size.w * scale, height: size.h * scale } : undefined;

  return createPortal(
    <div
      className="study-note-zoom"
      role="dialog"
      aria-modal="true"
      aria-label={target.kind === "diagram" ? "図の拡大" : "画像の拡大"}
    >
      <div
        ref={stageRef}
        className="study-note-zoom-stage"
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <div
          className={`study-note-zoom-content study-note-zoom-content--${target.kind}`}
          style={box}
        >
          {target.kind === "diagram" ? (
            <div
              ref={contentRef}
              className="study-note-zoom-diagram"
              // lib/mermaid.ts が securityLevel: strict で作り、プレビューに出した SVG をそのまま写す
              dangerouslySetInnerHTML={{ __html: target.svg }}
            />
          ) : (
            <img
              src={target.src}
              alt={target.alt}
              onLoad={(e) =>
                fitTo({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })
              }
            />
          )}
        </div>
      </div>
      <div className="study-note-zoom-bar">
        <button
          type="button"
          className="study-note-zoom-btn"
          aria-label="小さくする"
          disabled={scale <= MIN_SCALE}
          onClick={() => setScale((s) => clamp(s / STEP))}
        >
          −
        </button>
        <span className="study-note-zoom-scale" aria-live="polite">
          {Math.round(scale * 100)}%
        </span>
        <button
          type="button"
          className="study-note-zoom-btn"
          aria-label="大きくする"
          disabled={scale >= MAX_SCALE}
          onClick={() => setScale((s) => clamp(s * STEP))}
        >
          ＋
        </button>
        <button type="button" className="study-note-zoom-close" onClick={onClose}>
          閉じる
        </button>
      </div>
    </div>,
    document.body,
  );
}
